import { api } from '@/config/api';
import { tokenStorage } from '@/utils/jwt';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'won' | 'lost' | 'spam';
export type LeadSource =
  | 'ctwa'
  | 'lead_ad_form'
  | 'manual'
  | 'inbound_keyword'
  | 'email'
  | 'web_form'
  | 'tiktok_lead_ad'
  | 'google_lead_form'
  | 'google_ads';

export interface Lead {
  _id: string;
  leadUid: string;
  companyUid: string;
  contactUid?: string;
  accountUid?: string;
  lastMessage?: { body?: string; mediaType?: string; direction: string; createdAt: string; deliveryStatus?: string };
  phone: string;
  email?: string;
  countryCode?: string;
  profileName?: string;

  // Provenance
  source: LeadSource;
  sourceCampaignId?: string;
  sourceAdSetId?: string;
  sourceAdId?: string;
  sourceCreativeId?: string;
  sourceFormId?: string;
  sourceLeadgenId?: string;
  ctwaClid?: string;
  referralHeadline?: string;
  referralBody?: string;
  referralImageUrl?: string;
  /** Permanent ImageKit mirror of the CTWA referral image — prefer this. */
  referralImagekitUrl?: string;
  referralSourceUrl?: string;

  // Bot state
  botFlowUid?: string;
  currentStateId?: string;
  collectedFields: Record<string, string>;
  aiFallbackPaused?: boolean;

  /**
   * Last customer message timestamp — anchors WhatsApp's 24h customer-service
   * window. Free-form replies are only deliverable while this is <24h old.
   */
  lastInboundAt?: string;

  // Lifecycle
  status: LeadStatus;
  qualificationScore?: number;
  assignedToUserUid?: string;
  assignedToDepartmentUid?: string;
  notes?: string;
  estimatedValueAmount?: number;
  estimatedValueCurrency?: string;
  closedAt?: string;
  closedReason?: string;

  /** Follow-up SLA reminder — worker notifies the assignee once this passes. */
  nextFollowUpAt?: string;
  followUpNotifiedAt?: string;

  createdAt: string;
  updatedAt: string;
}

export interface LeadEvent {
  _id: string;
  eventUid: string;
  companyUid: string;
  leadUid: string;
  type: string;
  payload: Record<string, unknown>;
  actorUserUid?: string;
  actorKind: 'system' | 'bot' | 'ai' | 'user';
  createdAt: string;
}

export interface LeadDetail {
  lead: Lead;
  transcript: Array<{
    logUid?: string;
    type: string;
    phone: string;
    payload: Record<string, unknown>;
    createdAt: string;
  }>;
  leadEvents: LeadEvent[];
  hasOlderEvents?: boolean;
}

export interface ListLeadsResponse {
  items: Lead[];
  total: number;
  page: number;
  limit: number;
}

export interface ListEventsResponse {
  items: LeadEvent[];
  total: number;
  page: number;
  limit: number;
}

export interface AiDebugLog {
  debugLogUid: string;
  companyUid: string;
  leadUid: string;
  waMessageId?: string;
  leadEventUid?: string;
  provider?: string;
  model?: string;
  systemPrompt: string;
  transcript: Array<{ role: string; content?: string; toolCallId?: string }>;
  toolsOffered: Array<{ name: string; description?: string }>;
  firstReplyText?: string;
  firstReplyToolCalls: Array<{ id?: string; name: string; arguments: Record<string, unknown> }>;
  toolResults: Array<{ name: string; result: { ok: boolean; message: string; recordUid?: string; sideEffect?: string } }>;
  finalReplyText?: string;
  tokensIn: number;
  tokensOut: number;
  createdAt: string;
}

export interface UpdateLeadPayload {
  status?: LeadStatus;
  assignedToUserUid?: string;
  assignedToDepartmentUid?: string;
  notes?: string;
  qualificationScore?: number;
  estimatedValueAmount?: number;
  estimatedValueCurrency?: string;
  closedReason?: string;
  /** ISO date to schedule a follow-up; null clears it. */
  nextFollowUpAt?: string | null;
}

export interface ImportLeadsResult {
  created: number;
  skipped: number;
  errors: Array<{ row: number; reason: string }>;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const leadsService = {
  create: async (payload: { profileName?: string; phone?: string; email?: string; countryCode?: string; notes?: string }): Promise<{ lead: Lead; created: boolean }> => (await api.post('/slighter_crm/leads', payload)).data,
  openConversationStream: (): EventSource | null => {
    const token = tokenStorage.get();
    if (!token) return null;
    return new EventSource(`${api.defaults.baseURL}/slighter_crm/conversations/stream?access_token=${encodeURIComponent(token)}`);
  },
  olderMessages: async (leadUid: string, before: string): Promise<{ items: LeadEvent[]; hasMore: boolean }> =>
    (await api.get(`/slighter_crm/leads/${leadUid}/messages`, { params: { before } })).data,
  deleteConversation: async (leadUid: string): Promise<{ ok: boolean; deletedMessages: number }> =>
    (await api.delete(`/slighter_crm/leads/${leadUid}/conversation`)).data,

  list: async (params: {
    q?: string;
    conversationsOnly?: '1';
    status?: string;
    source?: LeadSource;
    sourceCampaignId?: string;
    sourceAdId?: string;
    assignedToUserUid?: string;
    assignedToDepartmentUid?: string;
    /** Scope to leads that arrived on this WhatsApp account (legacy null leads always included). */
    accountUid?: string;
    /** Pass "1" to filter to needs-attention only (status=new, no bot flow). */
    needsAttention?: string;
    /** ISO date — only leads with nextFollowUpAt <= this (pass now for "overdue"). */
    followUpBefore?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ListLeadsResponse> =>
    (await api.get('/slighter_crm/leads', { params })).data,

  /** Bulk CSV import — frontend-parsed rows + column mapping, max 2000 rows per request. */
  importCsv: async (payload: {
    filename?: string;
    mapping: Record<string, string>;
    rows: Record<string, string>[];
  }): Promise<ImportLeadsResult> =>
    (await api.post('/slighter_crm/leads/import', payload)).data,

  get: async (leadUid: string): Promise<LeadDetail> =>
    (await api.get(`/slighter_crm/leads/${leadUid}`)).data,

  events: async (leadUid: string, page = 1, limit = 50): Promise<ListEventsResponse> =>
    (await api.get(`/slighter_crm/leads/${leadUid}/events`, { params: { page, limit } })).data,

  update: async (leadUid: string, payload: UpdateLeadPayload): Promise<Lead> =>
    (await api.patch(`/slighter_crm/leads/${leadUid}`, payload)).data,

  remove: async (leadUid: string): Promise<{ ok: boolean }> =>
    (await api.delete(`/slighter_crm/leads/${leadUid}`)).data,

  /** Paginated list of AI debug logs for the conversation review screen. */
  listAiDebug: async (params: {
    page?: number;
    limit?: number;
    leadUid?: string;
  } = {}): Promise<{ items: AiDebugLog[]; total: number; page: number; limit: number }> =>
    (await api.get('/slighter_crm/leads/ai-debug', { params })).data,

  /** Single AI debug log by uid — full transcript + system prompt. */
  getAiDebug: async (debugLogUid: string): Promise<AiDebugLog> =>
    (await api.get(`/slighter_crm/leads/ai-debug/${debugLogUid}`)).data,

  /** Hand the lead off to another department (+ optional user inside). */
  handoverToDepartment: async (
    leadUid: string,
    payload: { deptUid: string; assignedToUserUid?: string; reason?: string },
  ): Promise<Lead> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/handover-department`, payload)).data,

  addNote: async (leadUid: string, body: string): Promise<{ ok: boolean }> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/notes`, { body })).data,

  reply: async (
    leadUid: string,
    payload: {
      body?: string;
      clientRequestId?: string;
      assetUid?: string; flowCta?: string; flowScreen?: string;
      audioUrl?: string; audioMime?: string; audioTranscript?: string;
      mediaUrl?: string; mediaType?: 'image' | 'video' | 'document'; mediaFilename?: string;
    },
  ): Promise<{ ok: boolean; waMessageId?: string; error?: string }> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/reply`, payload)).data,

  takeOver: async (leadUid: string): Promise<Lead> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/take-over`)).data,

  /** Mark the latest inbound message read on Meta (best-effort — never throws to caller). */
  markRead: async (leadUid: string): Promise<{ ok: boolean; markedMessageId?: string; error?: string }> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/mark-read`)).data,

  /** Phase 5: Atomic — pauses bot + assigns to caller + sets contacted in one shot */
  takeOverNow: async (leadUid: string): Promise<Lead> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/take-over-now`)).data,

  pauseBot: async (leadUid: string): Promise<Lead> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/pause-bot`)).data,

  resumeBot: async (leadUid: string): Promise<Lead> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/resume-bot`)).data,

  pauseAiFallback: async (leadUid: string): Promise<Lead> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/pause-ai-fallback`)).data,

  resumeAiFallback: async (leadUid: string): Promise<Lead> =>
    (await api.post(`/slighter_crm/leads/${leadUid}/resume-ai-fallback`)).data,
};
